// Корневой route: монтирует MainLayout + Toaster + view-transition ready Outlet.
// Foundation-shell. Расширяется AuthProvider, ThemeProvider, SocketProvider
// в Phase 1+ (см. rules/frontend-state.md §5).

import { Toaster } from "@studyhub/ui/components/sonner";
import { TooltipProvider } from "@studyhub/ui/components/tooltip";
import { createRootRoute, Outlet, ScrollRestoration, useRouterState } from "@tanstack/react-router";
import { type lazy, Suspense, useEffect } from "react";
import { AudioPlayerEngine } from "@/components/audio-recaps/audio-player-engine";
import { AuthFlowGate } from "@/components/auth/auth-flow-gate";
import { SessionRevokedListener } from "@/components/auth/session-revoked-listener";
import { SsoReturnHandler } from "@/components/auth/sso-return-handler";
import { PastDueBanner } from "@/components/billing/banners/past-due-banner";
import { TrialBanner } from "@/components/billing/banners/trial-banner";
import { PromoGrantedDialog } from "@/components/billing/dialogs/promo-granted-dialog";
import { NativeConsentSlot } from "@/components/consent/native-consent-slot";
import { LocaleRouteSync } from "@/components/i18n/locale-route-sync";
import { LocaleSync } from "@/components/i18n/locale-sync";
import { BannerStack } from "@/components/layout/banner-stack";
import {
	DeferredAddMenuDialog,
	DeferredAuthExpiredModal,
	DeferredCommandPalette,
	DeferredDiscoverDialog,
	DeferredHowItWorksDialog,
	DeferredLoginModal,
	DeferredManageSubscriptionDialog,
	DeferredTutorSidePanel,
	DeferredUpgradeModal,
	DeferredUploadDialog,
	useWarmDeferredGlobals,
} from "@/components/layout/deferred-globals";
import { ImpersonationBanner } from "@/components/layout/impersonation-banner";
import { MainLayout } from "@/components/layout/main-layout";
import { OfflineBanner } from "@/components/layout/offline-banner";
import { PendingSyncBanner } from "@/components/layout/pending-sync-banner";
import { SyncLibraryBanner } from "@/components/layout/sync-library-banner";
import { ModuleRouteGuard } from "@/components/modules/module-route-guard";
import { NativePushInit } from "@/components/notifications/native-push-init";
import { NotificationToastListener } from "@/components/notifications/notification-toast-listener";
import { PushInit } from "@/components/notifications/push-init";
// LiveBanner удалён — индикатор активной записи теперь в Topbar
// (components/recordings/recording-indicator.tsx). Прежний sticky-баннер
// перекрывался шапкой и сдвигал контент в layout-потоке.
import { NoIndexMeta } from "@/components/seo/NoIndexMeta";
import { ConfirmHost } from "@/components/shared/confirm-host";
import { GlossaryPopover } from "@/components/shared/glossary-popover";
import { CalendarRescheduleListener } from "@/components/study-plan/calendar/calendar-reschedule-listener";
import { SupportWidget } from "@/components/support/support-widget";
import { CreateFromChatReadyListener } from "@/components/tutor/create-from-chat-ready-listener";
import { UpdateBanner } from "@/components/update-banner";
import { useAppVersion } from "@/hooks/use-app-version";
import { useCmdK } from "@/hooks/use-cmd-k";
import { GlossaryDictionaryProvider } from "@/hooks/use-glossary-terms";
import { useOverlayBackGuard } from "@/hooks/use-overlay-back-guard";
import { initAppVisibleTracking } from "@/lib/app-visible";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { moduleOverlayShellPrefixes, moduleTutorSuppressedPrefixes } from "@/lib/module-registry";
import { recordNavigation } from "@/lib/observability/nav-trail";
import { ReplayGuard } from "@/lib/observability/replay-guard";
import { useAuth } from "@/providers/auth-provider";
import { ModuleAvailabilityProvider } from "@/providers/module-availability-provider";
import { RealtimeInvalidationProvider } from "@/providers/realtime-invalidation-provider";
import { RecorderProvider } from "@/providers/recorder-provider";

// Роуты, использующие собственный overlay-shell (без Sidebar / Topbar / Bottom Nav).
//
// Два источника, и они разной природы:
//   • ШЛЮЗЫ приложения — consent, onboarding-тур, вход под чужим аккаунтом,
//     публичный шаринг, биллинг-чекаут. Модулями они не являются (выключить их
//     нельзя, `ModuleId` им не положен), поэтому объявлены здесь;
//   • ПОЛНОЭКРАННЫЕ РЕЖИМЫ МОДУЛЕЙ — прохождение теста, тренировка карточек,
//     запись лекции, голосовая сессия. Их объявляет сам модуль
//     (`overlayShellPrefixes` в `modules/<id>.manifest.ts`), и новый модуль со
//     своим полноэкранным режимом попадает сюда без правки корневого роута.
const GATEWAY_OVERLAY_PREFIXES = [
	"/legal/accept",
	"/onboarding",
	"/impersonate",
	"/shared",
	"/billing/checkout",
	"/billing/success",
	"/billing/refund-status",
];

const OVERLAY_SHELL_PREFIXES = [...GATEWAY_OVERLAY_PREFIXES, ...moduleOverlayShellPrefixes()];

function isOverlayRoute(pathname: string): boolean {
	return OVERLAY_SHELL_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

// Маршруты, где AI-репетитор НЕ нужен и не должен фоном дёргать tutor.sessions.*:
// «шлюзовые» overlay-флоу (согласие/онбординг до принятия, биллинг-чекаут,
// impersonate-вход, публичный шаринг) плюс те полноэкранные режимы модулей,
// которые сами просят тишины (`tutorSuppressedPrefixes` в манифесте модуля —
// сегодня таких нет: запись лекции перестала подавлять панель 2026-08-17).
// На всех остальных (главное приложение + полноэкранные режимы тест/карточки/
// голос/запись) панель смонтирована глобально.
const TUTOR_SUPPRESSED_PREFIXES = [...GATEWAY_OVERLAY_PREFIXES, ...moduleTutorSuppressedPrefixes()];

function isTutorSuppressedRoute(pathname: string): boolean {
	// Воркспейс курса здесь БОЛЬШЕ НЕ ПОДАВЛЯЕТСЯ (этап 1 плана единого
	// репетитора). Отделение держалось на одной причине — скоупе на курс, — а
	// скоуп теперь умеет сама глобальная панель (`tutor-panel-course-scope.ts`:
	// состав курса, тема, шаг, плашка защищённых источников). Возврат условия
	// сюда означал бы возврат второй реализации репетитора.
	return TUTOR_SUPPRESSED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** /tutor/$sessionId (в т.ч. /tutor/new) — не список, не skills/scenarios. */
function isTutorChatSessionRoute(pathname: string): boolean {
	if (!pathname.startsWith("/tutor/")) return false;
	if (pathname === "/tutor/" || pathname === "/tutor") return false;
	if (pathname.startsWith("/tutor/skills") || pathname.startsWith("/tutor/scenarios")) {
		return false;
	}
	return true;
}

// Публичные маршруты, индексируемые поисковиками. Всё остальное по умолчанию
// noindex (PageMeta на конкретной странице может перебивать).
// `/` обрабатывается отдельно: guest = публичная, authenticated = noindex
// (см. routes/index.tsx).
const PUBLIC_EXACT = new Set(["/about", "/pricing", "/faq"]);
const PUBLIC_PREFIXES = ["/legal/"];

function isPublicRoute(pathname: string): boolean {
	if (PUBLIC_EXACT.has(pathname)) return true;
	if (PUBLIC_PREFIXES.some((p) => pathname.startsWith(p))) return true;
	return false;
}

// router-devtools только в dev-сборке (вырезается tree-shake'ом в prod).
// Временно отключён — мешает ручному тестированию (закрывает правый нижний угол).
const TanStackRouterDevtools: ReturnType<typeof lazy> | null = null;

import { NotFoundPage } from "@/components/layout/not-found-page";

export const Route = createRootRoute({
	component: RootComponent,
	notFoundComponent: NotFoundPage,
});

function RootComponent() {
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const overlay = isOverlayRoute(pathname);
	const commerceVisible = useCommerceVisible();
	// Дефолт robots для всех внутренних SPA-маршрутов. `/` решается в index.tsx
	// (guest = публичная), публичные маршруты сами обновят robots через PageMeta.
	const shouldNoIndexByDefault = pathname !== "/" && !isPublicRoute(pathname);
	// Cmd/Ctrl+K глобальный хоткей — открывает CommandPalette. Требует Router-контекста.
	useCmdK();
	// Polling /version.json для zero-downtime deploy (blue/green swap).
	// На Capacitor — no-op (там Capgo OTA). См. hooks/use-app-version.ts.
	useAppVersion();
	// Кнопка «Назад» (Android hardware back + browser back в mobile-web) сначала
	// закрывает открытый попап/шторку, а не уводит страницу позади.
	useOverlayBackGuard();
	// Репетитор и палитра команд грузятся отложенно (deferred-globals.tsx).
	// Вошедшему пользователю подтягиваем их на простое, чтобы первое открытие
	// было мгновенным; гостю на лендинге — не грузим вовсе.
	useWarmDeferredGlobals(useAuth().isAuthenticated);
	// Однократно поднимаем трекинг видимости приложения для presence-aware
	// подавления уведомлений (на web — no-op, на Capacitor — подписка app-state).
	useEffect(() => {
		initAppVisibleTracking();
	}, []);
	// След навигации для аналитики «откуда пришёл» (`lib/observability/nav-trail`).
	// Эффект ИМЕННО здесь, в корне: React выполняет эффекты детей раньше
	// родительских, поэтому экран успевает прочитать предыдущий путь до того,
	// как эта строка перезапишет его текущим.
	useEffect(() => {
		recordNavigation(pathname);
	}, [pathname]);

	return (
		// Единый глобальный TooltipProvider — все поясняющие тултипы на иконочных
		// кнопках по интерфейсу используют его (не плодят локальные провайдеры).
		// delayDuration небольшой, чтобы подсказка появлялась быстро на hover.
		<TooltipProvider delayDuration={300} skipDelayDuration={150}>
			{/* Словарь терминов — ОДИН watcher PowerSync на всё приложение; слой
			    подсветки читает его через useGlossaryIndex (см. rules §8). */}
			<GlossaryDictionaryProvider>
				<RecorderProvider>
					{shouldNoIndexByDefault && <NoIndexMeta />}
					<ReplayGuard />
					{/* Однократно сверяет язык UI с users.language и устраняет рассинхрон
				    (UI на одном языке, серверный контент — рекомендации/уведомления —
				    на другом). См. components/i18n/locale-sync.tsx. */}
					<LocaleSync />
					{/* Язык публичной страницы задаёт её адрес (`/de/about`), язык
					    закрытой части — настройка пользователя. Этот компонент
					    переключает между двумя режимами на переходах. */}
					<LocaleRouteSync />
					<AudioPlayerEngine />
					<PushInit />
					<NativePushInit />
					{/* Согласие на аналитику и рекламу в приложении (OXEM-604): веб-баннер
					    consent.js здесь не подключается, вопрос задаёт нативный экран. */}
					<NativeConsentSlot />
					<NotificationToastListener />
					{/* create-from-chat: *:ready → artifact_card + toast; poll fallback если socket пропущен */}
					<CreateFromChatReadyListener />
					<RealtimeInvalidationProvider />
					{/* Единственный писатель карты доступности модулей: запрос
					    `modules.availability`, офлайн-снапшот, реакция на смену
					    аккаунта. Читатели — `hooks/use-module-availability`. */}
					<ModuleAvailabilityProvider />
					<SsoReturnHandler />
					{/* Бан / admin force-logout / смена пароля: сервер шлёт session:revoked
				    → чистим локальную сессию и уводим на вход (план auth §7.5). */}
					<SessionRevokedListener />
					{/* Глобальный listener study-plan:calendar.ready — toast «Расписание
				    обновлено» + подсветка перемещённых уроков во всех поверхностях
				    (календарь / деталь плана / дашборд). См. rules §14 realtime. */}
					<CalendarRescheduleListener />
					{/* Impersonation-баннер виден ВСЕГДА (в т.ч. overlay-routes) — админ
			    обязан видеть, что работает под чужим аккаунтом (§8i). */}
					<BannerStack>
						<ImpersonationBanner />
						<OfflineBanner />
						{/* Есть сеть, но очередь застряла — иначе полное молчание (R6). */}
						<PendingSyncBanner />
						<SyncLibraryBanner />
						{/* Оба баннера — про оплату и ведут на `/settings/billing`.
						    Когда коммерция скрыта, этот роут закрыт, а сами тексты
						    («оплата не прошла», «пробный период истекает») говорят о
						    механике, которой в мобильной сборке нет. */}
						{!overlay && commerceVisible && <PastDueBanner />}
						{!overlay && commerceVisible && <TrialBanner />}
					</BannerStack>
					{overlay ? (
						<AuthFlowGate>
							{/* Overlay-routes (онбординг, study/take, voice-session) не имеют
						    <main> с резервом под баннеры. Когда активен ImpersonationBanner/
						    OfflineBanner (BannerStack рендерится и тут), сдвигаем контент на
						    его высоту, чтобы баннер не перекрыл собственную шапку overlay.
						    --banners-height = 0 без активных баннеров → обычно no-op. */}
							<div style={{ paddingTop: "var(--banners-height, 0px)" }}>
								{/* Полноэкранные режимы модулей — без плашки: каркас
								    фиксирован по высоте, места под баннер там нет. */}
								<ModuleRouteGuard withNotice={false}>
									<Outlet />
								</ModuleRouteGuard>
							</div>
						</AuthFlowGate>
					) : (
						<AuthFlowGate>
							<MainLayout>
								<ModuleRouteGuard>
									<Outlet />
								</ModuleRouteGuard>
							</MainLayout>
						</AuthFlowGate>
					)}
					{/* AI-репетитор: единый глобальный инстанс side-panel. Смонтирован вне
				    overlay-тернарника, чтобы работать и в полноэкранных режимах (тест /
				    карточки / голосовая сессия), где MainLayout не монтируется. На
				    desktop сам выставляет --tutor-push-width — overlay-контейнеры
				    отжимают контент; на mobile = full-screen Drawer поверх. Контекст
				    открытой страницы подхватывается автоматически (usePageContext). */}
					<DeferredTutorSidePanel suppressed={isTutorSuppressedRoute(pathname)} />
					<DeferredCommandPalette />
					<DeferredUpgradeModal />
					{/* Анонс промо-Pro: не показываем поверх overlay-флоу (onboarding/consent). */}
					{!overlay && <PromoGrantedDialog />}
					<DeferredManageSubscriptionDialog />
					<DeferredUploadDialog />
					<DeferredAddMenuDialog />
					<DeferredDiscoverDialog />
					<DeferredAuthExpiredModal />
					<DeferredLoginModal />
					{/* Справка «Как это работает»: единственный инстанс на приложение.
					    Точек входа несколько (кнопка дашборда, меню профиля, баннеры
					    разделов) — модалка одна, состояние в `stores/how-it-works.ts`. */}
					<DeferredHowItWorksDialog />
					<ConfirmHost />
					{/* Слой глоссария: единственный поповер на все поверхности
				    (делегированные hover/tap по [data-glossary]). */}
					<GlossaryPopover />
					<UpdateBanner />
					{/* Кнопка «Поддержка» с ботом StudyHub: виджет портала, здесь только
					    встраивание и правило страниц (components/support/*). */}
					<SupportWidget />
					<Toaster />
					{/* Tutor chat имеет собственный scroll-viewport (Gemini pin / open-settle).
				    Window ScrollRestoration делает scrollTo(0) при смене сессии и ломает UX. */}
					{!isTutorChatSessionRoute(pathname) && <ScrollRestoration />}
					{TanStackRouterDevtools && (
						<Suspense fallback={null}>
							<TanStackRouterDevtools position="bottom-right" />
						</Suspense>
					)}
				</RecorderProvider>
			</GlossaryDictionaryProvider>
		</TooltipProvider>
	);
}
